import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { CHART_AC, CHART_DC, CHART_NEUTRAL } from "../../../../styles/theme_constants";
import { formatNumber, perSession } from "../../../../utils/fleetDerivations";
import { axisLabel, splitDrawable } from "./panelHelpers";
import type { PanelProps } from "./panelHelpers";
import { baseChartOptions, CHART_HEIGHT, lineSeries } from "./dashboardChart";
import StatTile from "./StatTile";

/**
 * BOF-76 — average energy per charging session (FR-020 – FR-022).
 *
 * The denominator is sessions that delivered energy: a zero-energy session is excluded from its own
 * class's average, as in every other Station Performance view (FR-021). With no such sessions there is no
 * average, and the panel shows "—", never 0 (FR-022). A ratio is unaffected by the 30-day lens, because
 * its numerator and denominator scale together.
 */
const kwh = (v: number | null) => (v == null ? "—" : `${formatNumber(v, 1)} kWh`);

const PerSessionPanel: React.FC<PanelProps> = ({ selected, trend }) => {
  const split = splitDrawable(trend);
  const total = (p: (typeof trend)[number]) => perSession(p.energyKwh.total, p.sessions.qualifying);
  const ac = (p: (typeof trend)[number]) => perSession(p.energyKwh.ac, p.sessions.qualifyingAc);
  const dc = (p: (typeof trend)[number]) => perSession(p.energyKwh.dc, p.sessions.qualifyingDc);

  const data = useMemo(
    () => ({
      labels: trend.map(axisLabel),
      datasets: split
        ? [
            lineSeries("AC", trend.map(ac), CHART_AC),
            lineSeries("DC", trend.map(dc), CHART_DC),
            lineSeries("All sessions", trend.map(total), CHART_NEUTRAL, true),
          ]
        : [lineSeries("All sessions", trend.map(total), CHART_NEUTRAL)],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trend, split],
  );

  return (
    <Card title="Energy per session" className="h-full">
      <div className="grid mb-2">
        <div className="col-12 sm:col-4">
          <StatTile label="All sessions" value={kwh(total(selected))} hint="Excludes sessions that delivered no energy." />
        </div>
        {split && (
          <>
            <div className="col-6 sm:col-4">
              <StatTile label="AC" value={kwh(ac(selected))} />
            </div>
            <div className="col-6 sm:col-4">
              <StatTile label="DC" value={kwh(dc(selected))} />
            </div>
          </>
        )}
      </div>
      <Chart type="line" data={data} options={baseChartOptions({ yTitle: "kWh per session" })} style={CHART_HEIGHT} />
    </Card>
  );
};

export default PerSessionPanel;
